import { FontPairing } from 'constants/fontPairings'
import { useUpdateColorPalette } from 'hooks/queries/workspaces/brandVisualKits/useUpdateColorPalette'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { BrandKit, FontRole } from 'types'
import { reportError } from 'utils/error'
import { toHex } from './utils'

interface Props {
  fontPairing: FontPairing
  colors: string[]
  brandKit: BrandKit
  onNext: () => void
}

export const useHandleNext = ({ fontPairing, colors, brandKit, onNext }: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const colorMutation = useUpdateColorPalette({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    id: brandKit.brand_visual_kit?.primary_color_palette.id as number,
  })

  const titleMutation = useUpsertTextStyle({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    fontRole: FontRole.Title,
  })

  const bodyMutation = useUpsertTextStyle({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    fontRole: FontRole.Body,
  })

  const handleNext = async () => {
    setIsLoading(true)
    try {
      const titleStyle = fontPairing[0]
      const bodyStyle = fontPairing[1]

      if (titleStyle) {
        await titleMutation.mutateAsync({
          style: titleStyle.style,
          name: titleStyle.style.font_family,
        })
      }

      if (bodyStyle) {
        await bodyMutation.mutateAsync({
          style: bodyStyle.style,
          name: bodyStyle.style.font_family,
        })
      }

      await Promise.all([
        colorMutation.mutateAsync({
          colors: colors.map((color) => ({ hex: toHex(color), name: undefined })),
        }),
        bodyStyle &&
          bodyMutation.mutateAsync({
            style: bodyStyle.style,
            name: bodyStyle.style.font_family,
          }),
        titleStyle &&
          titleMutation.mutateAsync({
            style: titleStyle.style,
            name: titleStyle.style.font_family,
          }),
      ])

      await Promise.all([
        queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits']),
        queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandKits']),
      ])

      setIsLoading(false)
      onNext()
    } catch (error) {
      setIsLoading(false)
      reportError(error)
    }
  }

  return { handleNext, isLoading }
}
